import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { getIncomeTypeSettings, getCoupleSettings } from '../../services/roadmap.service';
import { verifyReferralCode, recordReferral, updateUserReferral, checkIfUserAlreadyReferred } from '../../services/franchise.service';

const Step1PersonalInfo = ({ data, updateData, nextStep, user }) => {
    const [formData, setFormData] = useState(data);
    const [errors, setErrors] = useState({});
    const isInitialMount = useRef(true);

    const [isPartnershipEnabled, setIsPartnershipEnabled] = useState(true);
    const [isCoupleEnabled, setIsCoupleEnabled] = useState(true);
    const [isVerifying, setIsVerifying] = useState(false);
    const [referralStatus, setReferralStatus] = useState(null); // 'success' or 'error'
    const [referralMessage, setReferralMessage] = useState('');
    const [franchiseName, setFranchiseName] = useState('');
    const [isReferralLocked, setIsReferralLocked] = useState(false);

    // If data already has a verified referral, lock it on mount
    useEffect(() => {
        if (data.referredBy) {
            setIsReferralLocked(true);
            setReferralStatus('success');
            setReferralMessage('Referral code verified successfully.');
            if (data.referredByName) {
                setFranchiseName(data.referredByName);
            }
        } else if (user?.referredBy || user?.franchiseReferralCode) {
            setIsReferralLocked(true);
            setReferralStatus('success');
            setReferralMessage('Referral code verified successfully.');
            if (user?.referredByName) {
                setFranchiseName(user.referredByName);
            }
            // Sync user data to formData
            setFormData(prev => ({
                ...prev,
                referredBy: user.referredBy || '',
                referredByName: user.referredByName || '',
                franchiseReferralCode: user.franchiseReferralCode || ''
            }));
        }
    }, [data.referredBy, data.referredByName, user?.referredBy, user?.referredByName, user?.franchiseReferralCode]);

    // Only sync with parent data on initial mount, not on every prop change
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            setFormData(data);
        }

        const fetchSettings = async () => {
            try {
                const [incomeSettings, coupleSettings] = await Promise.all([
                    getIncomeTypeSettings(),
                    getCoupleSettings()
                ]);
                setIsPartnershipEnabled(incomeSettings.partnershipEnabled);
                setIsCoupleEnabled(coupleSettings.coupleEnabled);
            } catch (error) {
                console.error('Error fetching income type settings:', error);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        
        // Reset referral status if they change the code and it's not locked
        if (name === 'franchiseReferralCode' && !isReferralLocked) {
            setReferralStatus(null);
            setReferralMessage('');
        }
    };

    useEffect(() => {
        const verifyCode = async () => {
            const code = formData.franchiseReferralCode?.trim();
            if (!code || isReferralLocked) return;
            
            // Check if user already has a referral code in local context
            if (user?.referredBy || user?.franchiseReferralCode || data.referredBy) {
                Swal.fire({
                    title: 'Already Used Referral Code',
                    text: 'You have already applied a referral code.',
                    icon: 'warning',
                    confirmButtonColor: '#1a7a5e'
                });
                setReferralStatus('error');
                setReferralMessage('Already used referral code.');
                setFormData(prev => ({
                    ...prev,
                    franchiseReferralCode: ''
                }));
                return;
            }

            setIsVerifying(true);
            setReferralStatus(null);
            setReferralMessage('');
            setFranchiseName('');

            // In-depth check against Firestore (by userId, email, or phone)
            const alreadyReferred = await checkIfUserAlreadyReferred(
                user?.id || user?.uid,
                formData.email?.trim(),
                formData.phone?.trim()
            );

            if (alreadyReferred) {
                setIsVerifying(false);
                Swal.fire({
                    title: 'Already Used Referral Code',
                    text: 'You have already applied a referral code.',
                    icon: 'warning',
                    confirmButtonColor: '#1a7a5e'
                });
                setReferralStatus('error');
                setReferralMessage('Already used referral code.');
                setFormData(prev => ({
                    ...prev,
                    franchiseReferralCode: ''
                }));
                return;
            }

            const result = await verifyReferralCode(code);
            
            setIsVerifying(false);

            if (result.isValid) {
                const adminName = result.data.fullName || result.data.franchiseName || result.data.name || 'Franchise Partner';
                setReferralStatus('success');
                setReferralMessage('Referral code verified successfully.');
                setFranchiseName(adminName);
                setIsReferralLocked(true);
                
                // Save the admin ID and name into formData so it goes to backend and persists locally
                setFormData(prev => ({
                    ...prev,
                    referredBy: result.adminId,
                    referredByName: adminName
                }));
            } else {
                setReferralStatus('error');
                setReferralMessage(result.error || 'Invalid referral code.');
            }
        };

        const timerId = setTimeout(() => {
            if (formData.franchiseReferralCode && formData.franchiseReferralCode.trim().length >= 4 && !isReferralLocked) {
                verifyCode();
            } else if (!formData.franchiseReferralCode?.trim() && !isReferralLocked) {
                setReferralStatus(null);
                setReferralMessage('');
                setFranchiseName('');
            }
        }, 800); // 800ms debounce

        return () => clearTimeout(timerId);
    }, [formData.franchiseReferralCode, isReferralLocked]);

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) {
            newErrors.email = 'Valid email is required';
        }

        if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
            newErrors.phone = 'Valid 10-digit phone number is required';
        }

        if (!formData.age) {
            newErrors.age = 'Age is required';
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }

        if (!formData.incomeType) {
            newErrors.incomeType = 'Income type is required';
        }

        if (formData.incomeType === 'salary' && !formData.familyType) {
            newErrors.familyType = 'Please select single or couple';
        }

        if (formData.incomeType === 'business' && !formData.businessCategory) {
            newErrors.businessCategory = 'Please select a business category';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            // Record initial activity if referred
            if (formData.referredBy && formData.name && formData.email) {
                // Determine if this is a newly verified code during this session
                // If the user object already had it, we don't necessarily need to "start" it again
                // But it's safe to call, as the backend will just update date/status
                await recordReferral(
                    formData.referredBy,
                    formData.name,
                    formData.email,
                    new Date().toLocaleDateString(),
                    'Started'
                );
                
                // Save it to user profile permanently if not already there
                if (user && user.id && !user.referredBy) {
                    await updateUserReferral(
                        user.id, 
                        formData.referredBy, 
                        formData.referredByName, 
                        formData.franchiseReferralCode
                    );
                }
            }
            
            updateData(formData);
            nextStep();
        }
    };

    return (
        <div className="form-step">
            <h2 className="step-title">Please Fill Your Details</h2>
            <p className="step-description">We need this to personalize your financial diagnosis report.</p>

            <div className="info-card">
                <div className="info-content">
                    <strong>Quick Tip:</strong> Your information is secure and will only be used to generate your personalized financial report. All fields marked with * are required.
                </div>
            </div>

            <form onSubmit={handleSubmit} className="diagnosis-form">
                {/* 1. Name */}
                <div className="form-group">
                    <label htmlFor="name">1. Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={errors.name ? 'error' : ''}
                        placeholder="Enter your name"
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                {/* 2. Email */}
                <div className="form-group">
                    <label htmlFor="email">2. Email Address *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                        placeholder="Enter your email"
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                {/* 3. Contact Number */}
                <div className="form-group">
                    <label htmlFor="phone">3. Contact Number *</label>
                    <div className="input-with-icon">
                        <span className="currency-symbol" style={{ left: '12px', fontSize: '0.9rem' }}>+91</span>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={errors.phone ? 'error' : ''}
                            placeholder="Enter 10-digit mobile number"
                            maxLength="10"
                            style={{ paddingLeft: '45px' }}
                            onWheel={(e) => {
                                e.target.blur();
                                e.stopPropagation();
                            }}
                            onKeyDown={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, arrows
                                if ([46, 8, 9, 27, 13, 37, 39].includes(e.keyCode) || // Removed 38 (Up), 40 (Down)
                                    // Allow: Ctrl+A, Command+A
                                    (e.key === 'a' && (e.ctrlKey === true || e.metaKey === true)) ||
                                    // Allow: home, end
                                    (e.keyCode >= 35 && e.keyCode <= 37) || e.keyCode === 39) {
                                    return;
                                }
                                // Stop Up/Down
                                if (e.keyCode === 38 || e.keyCode === 40) {
                                    e.preventDefault();
                                    return;
                                }
                                // Ensure that it is a number and stop the keypress
                                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </div>
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                {/* 4. Age (Years) */}
                <div className="form-group">
                    <label htmlFor="age">4. Age (Years) *</label>
                    <input
                        type="number"
                        id="age"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        className={errors.age ? 'error' : ''}
                        placeholder="Enter your age"
                        min="18"
                        max="120"
                        onWheel={(e) => {
                            e.target.blur();
                            e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                            if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.age && <span className="error-message">{errors.age}</span>}
                </div>

                {/* 5. Gender */}
                <div className="form-group">
                    <label htmlFor="gender">5. Gender *</label>
                    <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className={errors.gender ? 'error' : ''}
                    >
                        <option value="">Select your gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                    {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                {/* 5a. Franchise Referral Code (Optional) */}
                <div className="form-group">
                    <label htmlFor="franchiseReferralCode">Franchise Referral Code (Optional)</label>
                    <div className="referral-input-group">
                        <input
                            type="text"
                            id="franchiseReferralCode"
                            name="franchiseReferralCode"
                            value={formData.franchiseReferralCode || ''}
                            onChange={handleChange}
                            placeholder="Enter referral code if any"
                            disabled={isReferralLocked}
                            className={referralStatus === 'error' ? 'error' : referralStatus === 'success' ? 'success' : ''}
                            style={{ flex: 1, textTransform: 'uppercase' }}
                        />
                    </div>
                    {isVerifying && <span className="info-message" style={{ color: '#0284c7', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>Verifying code...</span>}
                    {referralStatus === 'success' && (
                        <div style={{ marginTop: '8px', padding: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            <div>
                                <div style={{ color: '#166534', fontSize: '0.85rem', fontWeight: 600 }}>{referralMessage}</div>
                                {franchiseName && <div style={{ color: '#15803d', fontSize: '0.8rem', marginTop: '2px' }}>Franchise Partner: <strong>{franchiseName}</strong></div>}
                            </div>
                        </div>
                    )}
                    {referralStatus === 'error' && <span className="error-message">{referralMessage}</span>}
                </div>

                {/* 6. Income Type */}
                <div className="form-group">
                    <label htmlFor="incomeType">6. Income Type *</label>
                    <select
                        id="incomeType"
                        name="incomeType"
                        value={formData.incomeType}
                        onChange={handleChange}
                        className={errors.incomeType ? 'error' : ''}
                    >
                        <option value="">Select your income type</option>
                        <option value="salary">Salary</option>
                        <option value="business">Business</option>
                        {isPartnershipEnabled && (
                            <option value="business_partnership">Business (partnership firm, llp, pvt ltd )</option>
                        )}
                    </select>
                    {errors.incomeType && <span className="error-message">{errors.incomeType}</span>}
                </div>

                {/* 6a. Single/Couple Dropdown (Shown only if Salary is selected) */}
                {formData.incomeType === 'salary' && (
                    <div className="form-group" style={{ marginLeft: '20px', borderLeft: '3px solid #6366f1', paddingLeft: '15px' }}>
                        <label htmlFor="familyType">Single/Couple *</label>
                        <select
                            id="familyType"
                            name="familyType"
                            value={formData.familyType || ''}
                            onChange={handleChange}
                            className={errors.familyType ? 'error' : ''}
                        >
                            <option value="">Select Status</option>
                            <option value="single">Single</option>
                            {isCoupleEnabled && (
                                <option value="couple">Couple</option>
                            )}
                        </select>
                        {errors.familyType && <span className="error-message">{errors.familyType}</span>}
                    </div>
                )}

                {/* 6b. Business Category (Shown only if Business (Proprietor) is selected) */}
                {formData.incomeType === 'business' && (
                    <div className="form-group" style={{ marginLeft: '20px', borderLeft: '3px solid #e5e7eb', paddingLeft: '15px' }}>
                        <label htmlFor="businessCategory">Business Category *</label>
                        <select
                            id="businessCategory"
                            name="businessCategory"
                            value={formData.businessCategory || ''}
                            onChange={handleChange}
                            className={errors.businessCategory ? 'error' : ''}
                        >
                            <option value="">Select Category</option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="services">Services</option>
                            <option value="trading">Trading</option>
                        </select>
                        {errors.businessCategory && <span className="error-message">{errors.businessCategory}</span>}
                    </div>
                )}

                <div className="form-actions">
                    <button type="submit" className="btn-primary">
                        Next: Income Details →
                    </button>
                </div>
            </form>

            <style jsx>{`
                .input-with-icon {
                    position: relative;
                }
                .currency-symbol {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6b7280;
                    font-weight: 500;
                }
                .input-with-icon input {
                    padding-left: 45px;
                }
                .referral-input-group {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }
                .btn-verify-referral {
                    padding: 10px 16px;
                    background: #1a7a5e;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }
                .btn-verify-referral:hover:not(:disabled) {
                    background: #135c46;
                }
                .btn-verify-referral:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                }
                input.success {
                    border-color: #22c55e;
                    background-color: #f0fdf4;
                }
            `}</style>
        </div>
    );
};

export default Step1PersonalInfo;

